import { lazy, Suspense, useState } from "react";
import { ATLAS_MODES, atlasMode, type LocationPresetId } from "../../../shared/atlas/modes";
import { ATLAS_PROVIDERS } from "../../../shared/atlas/providers";
import type { AtlasSettings } from "../../../shared/atlas/settings";
import type { AtlasModeId, ProviderHealth } from "../../../shared/atlas/types";
import { useHousehold } from "../../app/useHousehold";
import { useAtlasSettings, useAtlasStatus, useUpdateAtlasSettings } from "../../atlas/atlasQueries";
import { ageText } from "../../../shared/atlas/freshness";
import { Icon } from "../../ui/Icon";
import "./atlasAdmin.css";

const LocationMap = lazy(() => import("../../settings/LocationMap").then((m) => ({ default: m.LocationMap })));

const HEALTH: Record<ProviderHealth, string> = {
  ok: "Connected",
  stale: "Stale",
  error: "Error",
  "needs-key": "Needs a key",
  "license-review": "License review",
  "not-built": "Not built",
  unknown: "No requests yet",
};

const PRESETS: { id: LocationPresetId; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "colorado", label: "Colorado" },
  { id: "us", label: "United States" },
  { id: "world", label: "World" },
];

/** Admin → Living Atlas: modes, tour, performance, places, sources. */
export function AtlasSection() {
  const { isAdmin } = useHousehold();
  const { settings: s } = useAtlasSettings();
  const update = useUpdateAtlasSettings();
  const status = useAtlasStatus();
  const [msg, setMsg] = useState<string | null>(null);
  const [pick, setPick] = useState<{ lat: number; lon: number } | null>(null);
  const [placeName, setPlaceName] = useState("");
  const [openMode, setOpenMode] = useState<AtlasModeId | null>(null);

  const save = (patch: Partial<AtlasSettings>, done?: string) =>
    update.mutate(patch, { onSuccess: () => setMsg(done ?? null), onError: (e) => setMsg((e as Error).message) });
  const toggleIn = (list: AtlasModeId[], id: AtlasModeId) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const move = (id: AtlasModeId, dir: -1 | 1) => {
    const order = [...s.order];
    const i = order.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j]!, order[i]!];
    save({ order });
  };
  const clearDevice = () => {
    try {
      for (const k of Object.keys(localStorage)) if (k.startsWith("km:pref:atlas") || k.startsWith('km:cache:["atlas"')) localStorage.removeItem(k);
      setMsg("This browser’s saved Atlas views and cached data were cleared.");
    } catch {
      setMsg("This browser doesn’t allow clearing storage.");
    }
  };

  return (
    <div className="form atlas-admin">
      <h3>Living Atlas</h3>
      <p className="muted">A full-screen map of what’s happening nearby and around the world. Every mode is listed; modes without a lawful free source explain why instead of showing anything made up.</p>

      <label className="field field-toggle">
        <span>
          Show the Living Atlas
          <small className="muted">Adds it to the screen list on every display.</small>
        </span>
        <input type="checkbox" className="switch" checked={s.enabled} disabled={!isAdmin} onChange={(e) => save({ enabled: e.target.checked })} />
      </label>

      <div className="row wrap">
        <label className="field">
          <span>Opens on</span>
          <select className="input" value={s.defaultMode} disabled={!isAdmin} onChange={(e) => save({ defaultMode: e.target.value as AtlasModeId })}>
            {s.order.map((id) => (
              <option key={id} value={id}>
                {atlasMode(id)?.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Default place</span>
          <select className="input" value={s.defaultPreset} disabled={!isAdmin} onChange={(e) => save({ defaultPreset: e.target.value as LocationPresetId })}>
            {PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <h4>Modes</h4>
      <p className="muted small">Order, visibility, favorites (★) and which modes the tour visits. Per-mode default layers are under each mode.</p>
      <ul className="atlas-admin-modes">
        {s.order.map((id, i) => {
          const m = atlasMode(id)!;
          return (
            <li key={id}>
              <div className="atlas-admin-mode">
                <span className={`atlas-admin-dot ${m.availability}`} title={m.availability} />
                <strong>{m.name}</strong>
                <span className="muted small">{m.availability === "live" ? "Live data" : (m.unavailable?.title ?? m.availability)}</span>
                <span className="spacer" />
                <label className="atlas-admin-check" title="Shown in the switcher">
                  <input type="checkbox" checked={!s.hidden.includes(id)} disabled={!isAdmin} onChange={() => save({ hidden: toggleIn(s.hidden, id) })} /> Show
                </label>
                <label className="atlas-admin-check" title="Favorite">
                  <input type="checkbox" checked={s.favorites.includes(id)} disabled={!isAdmin} onChange={() => save({ favorites: toggleIn(s.favorites, id) })} /> ★
                </label>
                <label className="atlas-admin-check" title={m.availability === "live" ? "Include in the tour" : "Only live modes can be in the tour"}>
                  <input type="checkbox" checked={s.ambientModes.includes(id)} disabled={!isAdmin || m.availability !== "live"} onChange={() => save({ ambientModes: toggleIn(s.ambientModes, id) })} /> Tour
                </label>
                <button className="icon-btn" onClick={() => move(id, -1)} disabled={!isAdmin || i === 0} aria-label={`Move ${m.name} up`}>
                  <Icon name="chevronDown" size="1.1rem" className="flip" />
                </button>
                <button className="icon-btn" onClick={() => move(id, 1)} disabled={!isAdmin || i === s.order.length - 1} aria-label={`Move ${m.name} down`}>
                  <Icon name="chevronDown" size="1.1rem" />
                </button>
                {m.availability === "live" && (
                  <button className="link-btn" onClick={() => setOpenMode(openMode === id ? null : id)} aria-expanded={openMode === id}>
                    Layers
                  </button>
                )}
              </div>
              {openMode === id && (
                <div className="atlas-admin-layers">
                  {m.layers.map((l) => {
                    const cur = s.layerDefaults[id]?.[l.id] ?? { on: l.defaultVisible, opacity: l.opacity };
                    const set = (next: { on: boolean; opacity: number }) => save({ layerDefaults: { ...s.layerDefaults, [id]: { ...(s.layerDefaults[id] ?? {}), [l.id]: next } } });
                    return (
                      <div key={l.id} className="row">
                        <label className="atlas-admin-check">
                          <input type="checkbox" checked={cur.on} disabled={!isAdmin} onChange={(e) => set({ ...cur, on: e.target.checked })} /> {l.label}
                        </label>
                        <input type="range" min={0.1} max={1} step={0.05} value={cur.opacity} disabled={!isAdmin} aria-label={`${l.label} default opacity`} onChange={(e) => set({ ...cur, opacity: Number(e.target.value) })} />
                      </div>
                    );
                  })}
                  <p className="muted small">Each screen can still change layers for itself; these are the starting choices. Distances follow the household’s units (°F → miles, °C → km).</p>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <h4>Tour</h4>
      <div className="row wrap">
        <label className="field">
          <span>Next mode every (seconds)</span>
          <input className="input" type="number" min={20} max={1800} defaultValue={s.ambientIntervalSec} key={`i${s.ambientIntervalSec}`} disabled={!isAdmin} onBlur={(e) => Number(e.target.value) !== s.ambientIntervalSec && save({ ambientIntervalSec: Number(e.target.value) })} />
        </label>
        <label className="field">
          <span>Resume after a touch (seconds)</span>
          <input className="input" type="number" min={15} max={3600} defaultValue={s.ambientResumeSec} key={`r${s.ambientResumeSec}`} disabled={!isAdmin} onBlur={(e) => Number(e.target.value) !== s.ambientResumeSec && save({ ambientResumeSec: Number(e.target.value) })} />
        </label>
      </div>

      <h4>Performance</h4>
      <label className="field field-toggle">
        <span>
          Low-power mode
          <small className="muted">For a Raspberry Pi with 2 GB: fewer animation frames, no globe, no roads, smaller tile cache, 1× pixels. Screens with “Reduce effects” on use it automatically.</small>
        </span>
        <input type="checkbox" className="switch" checked={s.lowPower} disabled={!isAdmin} onChange={(e) => save({ lowPower: e.target.checked })} />
      </label>
      <label className="field field-toggle">
        <span>
          Reduce motion
          <small className="muted">No pulses, flights or auto-play, on every screen. Screens whose system asks for reduced motion get this anyway.</small>
        </span>
        <input type="checkbox" className="switch" checked={s.reducedMotion} disabled={!isAdmin} onChange={(e) => save({ reducedMotion: e.target.checked })} />
      </label>
      <label className="field field-toggle">
        <span>
          Show the performance panel to admins
          <small className="muted">Frame rate, memory, tiles, map errors and data age, on screens where an admin is signed in.</small>
        </span>
        <input type="checkbox" className="switch" checked={s.debugPanel} disabled={!isAdmin} onChange={(e) => save({ debugPanel: e.target.checked })} />
      </label>

      <h4>Places</h4>
      <p className="muted small">Extra location buttons next to Home, Colorado, United States and World. Stored to about 1 km.</p>
      {s.places.length > 0 && (
        <ul className="spot-admin">
          {s.places.map((p) => (
            <li key={p.id}>
              <Icon name="pin" size="1.1rem" />
              <strong>{p.name}</strong>
              <span className="muted small">
                {p.lat.toFixed(2)}, {p.lon.toFixed(2)} · zoom {p.zoom}
              </span>
              {isAdmin && (
                <button className="icon-btn" onClick={() => save({ places: s.places.filter((x) => x.id !== p.id) })} aria-label={`Remove ${p.name}`}>
                  <Icon name="trash" size="1.1rem" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {isAdmin && s.places.length < 12 && (
        <>
          <Suspense fallback={<div className="location-map" />}>
            <LocationMap lat={pick?.lat ?? 39.5} lon={pick?.lon ?? -105.5} zoom={5} onPick={(lat, lon) => setPick({ lat, lon })} />
          </Suspense>
          {pick && (
            <div className="row">
              <input className="input" value={placeName} maxLength={40} onChange={(e) => setPlaceName(e.target.value)} placeholder="Name, e.g. Grandma’s or Rocky Mountain NP" />
              <button
                className="btn btn-primary"
                disabled={!placeName.trim()}
                onClick={() =>
                  save({ places: [...s.places, { id: `place_${Date.now().toString(36)}`, name: placeName.trim(), lat: pick.lat, lon: pick.lon, zoom: 8 }] }, "Place added")
                }
              >
                Add place
              </button>
            </div>
          )}
        </>
      )}

      <h4>Sources & licensing</h4>
      <p className="muted small">Health is from the server’s last requests. “License review” means the terms haven’t been cleared for commercial distribution.</p>
      <div className="atlas-admin-table" role="table" aria-label="Atlas data sources">
        {(status.data?.providers ?? ATLAS_PROVIDERS.map((p) => ({ id: p.id, name: p.name, health: "unknown" as ProviderHealth, licenseStatus: p.licenseStatus, lastSuccessAt: null, lastError: null, requestsToday: undefined, note: p.redistribution, modes: p.modes }))).map((p) => (
          <div key={p.id} role="row" className="atlas-admin-source">
            <span role="cell" className={`atlas-admin-health ${p.health}`}>
              {HEALTH[p.health]}
            </span>
            <span role="cell">
              <strong>{p.name}</strong>
              <small className="muted">
                {p.modes.map((m) => atlasMode(m)?.name).join(", ") || "Base map"} · license {p.licenseStatus}
                {p.lastSuccessAt ? ` · last success ${ageText(p.lastSuccessAt)}` : ""}
                {p.requestsToday !== undefined ? ` · ${p.requestsToday} requests today` : ""}
              </small>
              {p.lastError && <small className="error-text">Last error: {p.lastError}</small>}
              <small className="muted">{p.note}</small>
            </span>
          </div>
        ))}
      </div>

      <h4>Keys (publisher only)</h4>
      <p className="muted small">
        Households never enter keys. Optional keys are Worker secrets set by the publisher; this page only shows whether they exist.
      </p>
      <ul className="kv-list">
        <li>
          <strong>NASA FIRMS MAP_KEY</strong> — {status.data?.keys ? (status.data.keys.firmsMapKey ? "configured" : "not set (Wildfires covers the U.S. and Hawaii without it)") : "—"}
          {isAdmin && status.data?.keys && !status.data.keys.firmsMapKey && (
            <small className="muted">
              {" "}
              Get a free key at firms.modaps.eosdis.nasa.gov/api/map_key, then run <code>wrangler secret put FIRMS_MAP_KEY</code>.
            </small>
          )}
        </li>
      </ul>

      <h4>Local sensors</h4>
      <p className="muted small">An ADS-B receiver (Airspace Live) or a lightning sensor (Lightning Live) will connect through the Kinmantle local agent on your network. That bridge isn’t available yet; nothing on this page needs hardware.</p>

      <h4>This browser</h4>
      <div className="row">
        <button className="btn" onClick={clearDevice}>
          <Icon name="trash" size="1.1rem" /> Clear saved Atlas views & cached data here
        </button>
      </div>
      <p className="muted small">The server caches each source briefly and keeps one last-good copy per source; map tiles are cached by the browser only.</p>

      {msg && <p className={update.isError ? "error-text" : "muted"}>{msg}</p>}
      <p className="muted small">{ATLAS_MODES.length} modes · no paid sources · works with AI turned off.</p>
    </div>
  );
}
